import React, { useState } from 'react';
import { Settings, Save, RotateCcw, Shield, AlertCircle } from 'lucide-react';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';

/**
 * CollegeTargetSettings - Allow customization of college category targets
 * Supports both student and parent/advisor override capabilities
 */
const CollegeTargetSettings = ({ 
  isOpen, 
  onClose,
  className = '' 
}) => {
  const { currentUser } = useFamilyContext();
  const { 
    customTargets, 
    setCustomTarget, 
    lockedCategories,
    saveCompletionLocks 
  } = useCollegeContext();
  
  const [localTargets, setLocalTargets] = useState(customTargets || {
    reach: 4,
    target: 4,
    safety: 4
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const isParent = currentUser?.type === 'parent';
  const defaultTargets = { reach: 4, target: 4, safety: 4 };

  const categoryConfig = {
    reach: { 
      title: 'Reach Schools', 
      description: 'Reach schools where admission is competitive',
      min: 2,
      max: 8,
      color: 'violet'
    },
    target: { 
      title: 'Target Schools', 
      description: 'Schools that match your academic profile',
      min: 3,
      max: 10,
      color: 'azure'
    },
    safety: { 
      title: 'Safety Schools', 
      description: 'Schools where admission is likely',
      min: 1,
      max: 6,
      color: 'emerald'
    }
  };

  const handleTargetChange = (category, value) => {
    const numValue = parseInt(value) || 0;
    const config = categoryConfig[category];
    const clampedValue = Math.min(Math.max(numValue, config.min), config.max);
    
    setLocalTargets(prev => ({
      ...prev,
      [category]: clampedValue
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Save each target individually using the setCustomTarget function
      for (const [category, target] of Object.entries(localTargets)) {
        await setCustomTarget(category, target);
      }
      
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving custom targets:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalTargets(defaultTargets);
    setHasChanges(true);
  };

  const getTotalTarget = () => {
    return Object.values(localTargets).reduce((sum, target) => sum + target, 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-xl shadow-xl max-w-md w-full mx-4 ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-azure-100 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-azure-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">College List Goals</h2>
              <p className="text-sm text-stone-500">Customize your college application targets</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-stone-400 hover:text-slate-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-6">
          {/* Parent/Advisor Notice */}
          {isParent && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-900">Parent/Advisor Override</h4>
                  <p className="text-sm text-amber-800 mt-1">
                    You can adjust these targets to guide your student's college list development.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Target Settings */}
          <div className="space-y-4">
            {Object.entries(categoryConfig).map(([key, config]) => {
              const currentTarget = localTargets[key];
              const isLocked = lockedCategories[key];
              
              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-900">{config.title}</h4>
                        {isLocked && (
                          <span className="px-2 py-1 bg-azure-100 text-azure-800 text-xs rounded-full">
                            Locked
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-stone-500">{config.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 sm:gap-4">
                    <input
                      type="range"
                      min={config.min}
                      max={config.max}
                      value={currentTarget}
                      onChange={(e) => handleTargetChange(key, e.target.value)}
                      className={`flex-1 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer slider-${config.color}`}
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={config.min}
                        max={config.max}
                        value={currentTarget}
                        onChange={(e) => handleTargetChange(key, e.target.value)}
                        className="w-16 px-2 py-1 border border-stone-300 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-azure-500"
                      />
                      <span className="text-sm text-stone-500">schools</span>
                    </div>
                  </div>
                  
                  <div className="text-xs text-stone-400">
                    Range: {config.min}-{config.max} schools
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total Summary */}
          <div className="bg-stone-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-slate-900">Total College List Goal</span>
              <span className="text-xl font-bold text-azure-600">{getTotalTarget()} schools</span>
            </div>
            <div className="text-sm text-stone-500 mt-1">
              {getTotalTarget() < 8 && "Consider adding more schools for better options"}
              {getTotalTarget() >= 8 && getTotalTarget() <= 15 && "Good balance for most students"}
              {getTotalTarget() > 15 && "This is quite ambitious - ensure quality over quantity"}
            </div>
          </div>

          {/* Recommendation Note */}
          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-azure-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-azure-900">Recommendation</h4>
                <p className="text-sm text-azure-800 mt-1">
                  Most students apply to 8-12 colleges total. Quality research is more important than quantity.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-stone-200">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Default
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-3 sm:px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="px-3 sm:px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* CSS for custom slider colors */}
      <style jsx>{`
        .slider-violet::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
        }
        .slider-azure::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
        }
        .slider-emerald::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default CollegeTargetSettings;